import React, { useMemo } from "react";
import { Button, Form, Select } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import useDepartment from "../../hooks/useDepartment";
import useLang from "../../hooks/useLang";
import { toTemplateId } from "../../helpers/templateHelpers";
import { ALL_DEPARTMENTS_VALUE } from "../../constant/TemplateConstant";

/**
 * One `{ locationId, departmentIds | allDepartments }` entry.
 *
 * Its own component because the department list is fetched per location — a
 * hook cannot be called inside the Form.List map.
 */
const DepartmentScopeRow = ({ form, name, locationOptions, onRemove, canRemove }) => {
  const [lang] = useLang();
  const locationId = Form.useWatch(["scopes", name, "locationId"], form);

  const { departments, fetching } = useDepartment({
    locationId: toTemplateId(locationId),
    customSkipCondition: !toTemplateId(locationId),
  });

  const departmentOptions = useMemo(
    () => [
      { value: ALL_DEPARTMENTS_VALUE, label: "All departments" },
      ...(departments || []).map(department => ({
        value: String(department.id),
        label: department.name || `Department ${department.id}`,
      })),
    ],
    [departments]
  );

  // Locations already spoken for by another row — a location can only appear
  // once, otherwise its two entries would contradict each other.
  const takenLocationIds = (form.getFieldValue("scopes") || [])
    .filter((_, index) => index !== name)
    .map(scope => String(scope?.locationId));

  const setRow = patch => {
    const scopes = [...(form.getFieldValue("scopes") || [])];
    scopes[name] = { ...scopes[name], ...patch };
    form.setFieldsValue({ scopes });
  };

  return (
    <div className="flex flex-col sm:flex-row gap-2 items-start">
      <Form.Item
        className="flex-1 min-w-0 mb-0"
        name={[name, "locationId"]}
        rules={[{ required: true, message: "Select a location" }]}
      >
        <Select
          options={locationOptions.map(option => ({
            ...option,
            disabled: takenLocationIds.includes(option.value),
          }))}
          placeholder={lang.location}
          showSearch
          optionFilterProp="label"
          // Departments belong to a location, so the previous picks are void.
          onChange={() => setRow({ departmentIds: [] })}
        />
      </Form.Item>

      <Form.Item className="flex-1 min-w-0 mb-0" name={[name, "departmentIds"]}>
        <Select
          mode="multiple"
          options={departmentOptions}
          loading={fetching}
          disabled={!toTemplateId(locationId)}
          placeholder={lang.selectDepartments}
          allowClear
          showSearch
          optionFilterProp="label"
          maxTagCount="responsive"
          notFoundContent={fetching ? "Loading departments..." : undefined}
          // "All departments" and a hand-picked list are mutually exclusive:
          // picking one clears the other so the row cannot claim both.
          onChange={value => {
            const selected = value || [];
            const justPicked = selected[selected.length - 1];
            setRow({
              departmentIds:
                justPicked === ALL_DEPARTMENTS_VALUE
                  ? [ALL_DEPARTMENTS_VALUE]
                  : selected.filter(item => item !== ALL_DEPARTMENTS_VALUE),
            });
          }}
        />
      </Form.Item>

      <Button
        type="text"
        danger
        className="shrink-0"
        icon={<DeleteOutlined />}
        disabled={!canRemove}
        onClick={onRemove}
      />
    </div>
  );
};

const TemplateDepartmentScopes = ({ form, locationOptions = [] }) => {
  const [lang] = useLang();

  return (
    <Form.List name="scopes">
      {(fields, { add, remove }) => (
        <div className="flex flex-col gap-3">
          {fields.map(({ key, name }) => (
            <DepartmentScopeRow
              key={key}
              form={form}
              name={name}
              locationOptions={locationOptions}
              onRemove={() => remove(name)}
              canRemove={fields.length > 1}
            />
          ))}
          <Button
            type="link"
            className="self-start px-0"
            onClick={() => add({ locationId: null, departmentIds: [], allDepartments: false })}
            disabled={fields.length >= locationOptions.length}
          >
            {lang.addLocationAction}
          </Button>
        </div>
      )}
    </Form.List>
  );
};

export default TemplateDepartmentScopes;
